import { storeGet, storeSet } from '../store';
import logMsgWithSentry from '../helpers/logMsgWithSentry';
import { isEmpty } from 'ramda';

export class NetworkService {

    /**
     * The API answers 403 both for a dead session and for a permission denial
     * ("User is not part of the project", "Accesso riservato agli
     * amministratori"). Only the first one may end the session: a permission
     * denial has to reach the caller so the UI can disable the action instead
     * of logging the user out.
     */
    static isSessionError(resp) {
        const detail = resp?.detail ?? resp?.message;

        if (Array.isArray(detail) || detail === undefined || detail === null) {
            return false;
        }

        return /invalid token|token (scaduto|expired|non valido)|not authenticated|credentials/i
            .test(String(detail));
    }

    static logApiError = (endpoint, status = 0, resp = undefined) => {
        if (status === 500) {
            logMsgWithSentry(endpoint, status, resp);
            return false;
        } else if (status === 403) {
            // `resp === undefined` means the body has not been read yet: wait for
            // the second call, which can tell a dead session from a denied action.
            if (resp === undefined || !this.isSessionError(resp)) {
                return false;
            }

            storeSet('token', '');
            const { pathname } = window.location;
            if (!['/login', '/registration', '/reset-password'].includes(pathname)) {
                window.location.replace('/login');
            }
            return true;
        }
        return false;
    }

    static isNotBlank(value) {
        return value !== null && value !== undefined && value !== ''
    }

    /**
     * Build query string from params array. Values are percent-encoded: some
     * params carry free text (a search query, an assignment note) and would
     * otherwise break the URL on a space, '&' or a newline.
     * @param {Array} queryParams - Array of [key, value] pairs
     * @returns {string} Query string starting with '?' or empty string
     */
    static buildQueryString(queryParams) {
        if (!queryParams || queryParams.length === 0) {
            return '';
        }

        const params = queryParams
            .filter(([key, value]) => this.isNotBlank(key) && this.isNotBlank(value))
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return params ? `?${params}` : '';
    }

    /**
     * Async GET request
     */
    static async getAsync(url, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        // Skip request if it's /user/me and no token - handle same as 403
        if (fullUrl.includes('user/me') && isEmpty(storeGet('getToken'))) {
            storeSet('token', '');
            const { pathname } = window.location;
            if (!['/login', '/registration', '/reset-password'].includes(pathname)) {
                window.location.replace('/login');
            }
            return Promise.reject({ handled: true });
        }

        const response = await fetch(fullUrl, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async POST request with JSON body
     */
    static async postAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async POST request with multipart/form-data body
     */
    static async postMultiPartAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: body
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async PUT request with multipart/form-data body
     */
    static async putMultiPartAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: body
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async POST request returning blob
     */
    static async postBlobAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.blob();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async GET request returning blob
     */
    static async getBlobAsync(url, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.blob();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async PUT request
     */
    static async putAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async PATCH request
     */
    static async patchAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'PATCH',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async DELETE request
     */
    static async deleteAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        // A delete may legitimately answer with an empty body (e.g. 204).
        const text = await response.text();
        const data = text ? JSON.parse(text) : null;

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async POST request without authorization (for login/register)
     */
    static async unauthorizedPostAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    /**
     * Async PUT request without authorization (for password reset)
     */
    static async unauthorizedPutAsync(url, body, queryParams = null) {
        const queryString = this.buildQueryString(queryParams);
        const fullUrl = `${url}${queryString}`;

        const response = await fetch(fullUrl, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        const status = response.status;
        const wasHandled = this.logApiError(fullUrl, status);

        const data = await response.json();

        if (status >= 400 && status <= 599) {
            const wasHandledWithData = this.logApiError(fullUrl, status, data);

            if (wasHandled || wasHandledWithData) {
                return Promise.reject({ handled: true });
            }

            const error = new Error('Request failed');
            error.status = status;
            error.data = data;
            error.response = response;
            throw error;
        }

        return data;
    }

    // Legacy callback-based methods below - DEPRECATED, use async methods above

    static post = (url, body, callback, errorCallback, queryParams) => {

        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&') {
                url = url.substring(0, url.length - 1);
            }
        }

        fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static postMultiPart = (url, body, callback, errorCallback, queryParams) => {

        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
                //'Content-Type': 'multipart/form-data',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: body
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static postBlob = (url, body, callback, errorCallback, queryParams) => {

        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.blob(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static unauthorizedPost = (url, body, callback, errorCallback, queryParams) => {
        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static unauthorizedPut = (url, body, callback, errorCallback, queryParams) => {
        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static put = (url, body, callback, errorCallback, queryParams = null) => {
        if (queryParams) {
            url += '?'

            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static get = (url, callback, errorCallback, queryParams = null) => {
        if (queryParams) {
            url += '?'

            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        if (url.includes('user/me') && isEmpty(storeGet('getToken'))) {
            return;
        }

        fetch(url, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            //signal: AbortSignal.timeout(5000)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response);
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => {
                errorCallback(err)
            });

    };

    static getBlob = (url, callback, errorCallback, queryParams) => {

        if (queryParams) {
            url += '?'
            for (let i = 0; i < queryParams.length; i++) {
                if (queryParams[i] && this.isNotBlank(queryParams[i][0]) && this.isNotBlank(queryParams[i][1])) {
                    let param = queryParams[i][0] + '=' + queryParams[i][1]

                    if (i !== queryParams.length - 1)
                        param += '&'

                    url += param;
                }
            }

            if (url.charAt(url.length) === '&')
                url = url.substring(0, url.length - 1);
        }

        fetch(url, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.blob(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));
    };

    static delete = (url, body, callback, errorCallback, queryParams = null) => {
        if (queryParams) {
            let params = '?'

            for (let i = 0; i < queryParams.length; i++) {
                params += queryParams[i][0] + '=' + queryParams[i][1]
                if (queryParams.length !== i + 1)
                    params += '&'
                url += params
                params = ''
            }

        }
        fetch(url, {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + storeGet('getToken'),
            },
            body: JSON.stringify(body)
        })
            .then(async response => {
                let status = response.status;
                this.logApiError(url, status);
                return { response: await response.json(), status: status }
            })
            .then(data => {
                if (data.status >= 400 && data.status <= 599) {
                    errorCallback(data.response)
                    this.logApiError(url, data.status, data.response);
                } else {
                    callback(data.response)
                }
            })
            .catch(err => errorCallback(err));

    };
}
